import React, { useEffect } from 'react';
import { Users } from 'lucide-react';
import type { RoomPreference, TravelerInfo } from '../../../types/business';

interface RoomAssignmentProps {
  roomPreferences: RoomPreference[];
  travelers: TravelerInfo[];
  assignments: Record<string, string[]>;
  onAssign: (assignments: Record<string, string[]>) => void;
}

export default function RoomAssignment({ 
  roomPreferences, 
  travelers,
  assignments,
  onAssign,
}: RoomAssignmentProps) {
  
  // Initialiser les assignations si elles sont vides
  useEffect(() => {
    if (Object.keys(assignments).length === 0 && roomPreferences.length > 0) {
      // Créer un objet d'assignations initial avec des tableaux vides
      const initialAssignments = {};
      let roomIndex = 0;
      
      roomPreferences.forEach((room, typeIndex) => {
        for (let i = 0; i < room.count; i++) {
          initialAssignments[roomIndex.toString()] = [];
          roomIndex++;
        }
      });
      
      onAssign(initialAssignments);
    }
  }, [roomPreferences, assignments, onAssign]);

  const handleAssignTraveler = (roomIndex: string, travelerIndex: string) => {
    // Vérifier si le voyageur est déjà assigné à une autre chambre
    const isAssignedElsewhere = Object.entries(assignments).some(
      ([currentRoomIndex, travelers]) => 
        currentRoomIndex !== roomIndex && 
        travelers.includes(travelerIndex)
    );

    // Si le voyageur est déjà assigné ailleurs, le retirer de cette chambre
    if (isAssignedElsewhere) {
      const newAssignments = { ...assignments };
      
      Object.keys(newAssignments).forEach(currentRoomIndex => {
        if (currentRoomIndex !== roomIndex) {
          newAssignments[currentRoomIndex] = newAssignments[currentRoomIndex].filter(
            index => index !== travelerIndex
          );
        }
      });
      
      // Ajouter le voyageur à la nouvelle chambre
      if (!newAssignments[roomIndex]) {
        newAssignments[roomIndex] = [];
      }
      
      newAssignments[roomIndex] = [...newAssignments[roomIndex], travelerIndex];
      onAssign(newAssignments);
    } else {
      // Vérifier si le voyageur est déjà dans cette chambre
      const isAlreadyAssigned = assignments[roomIndex]?.includes(travelerIndex);
      
      const newAssignments = { ...assignments };
      
      if (!newAssignments[roomIndex]) {
        newAssignments[roomIndex] = [];
      }
      
      // Si le voyageur est déjà dans cette chambre, le retirer
      if (isAlreadyAssigned) {
        newAssignments[roomIndex] = newAssignments[roomIndex].filter(
          index => index !== travelerIndex
        );
      } else {
        // Sinon, l'ajouter
        newAssignments[roomIndex] = [...newAssignments[roomIndex], travelerIndex];
      }
      
      onAssign(newAssignments);
    }
  };

  const getAvailableRooms = (travelerId: string) => {
    return roomPreferences.flatMap(room => {
      const capacity = room.type === 'single' ? 1 : 
                      room.type === 'double' ? 2 :
                      room.type === 'triple' ? 3 : 2;

      return Array.from({ length: room.count }, (_, index) => {
        const roomId = `${room.type}-${index}`;
        const currentOccupants = assignments[roomId]?.length || 0;
        return {
          id: roomId,
          type: room.type,
          capacity,
          available: currentOccupants < capacity || assignments[roomId]?.includes(travelerId),
        };
      });
    });
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium">Attribution des chambres</h3>
      
      <div className="space-y-4">
        {travelers.map((traveler, index) => {
          const availableRooms = getAvailableRooms(`traveler-${index}`);
          const currentRoom = Object.entries(assignments).find(([_, occupants]) => 
            occupants.includes(`traveler-${index}`)
          )?.[0];

          return (
            <div key={index} className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  <span className="font-medium">
                    {traveler.firstName} {traveler.lastName}
                  </span>
                </div>
                <select
                  value={currentRoom || ''}
                  onChange={(e) => handleAssignTraveler(e.target.value, `traveler-${index}`)}
                  className="px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">Sélectionner une chambre</option>
                  {availableRooms.map(room => (
                    <option 
                      key={room.id} 
                      value={room.id}
                      disabled={!room.available}
                    >
                      Chambre {room.type === 'single' ? 'Simple' :
                              room.type === 'double' ? 'Double' :
                              room.type === 'triple' ? 'Triple' : 'Suite'} #{room.id.split('-')[1]}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          );
        })}
      </div>

      {/* Aperçu des chambres */}
      <div className="mt-8">
        <h4 className="font-medium mb-4">Aperçu des chambres</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {roomPreferences.flatMap(room => (
            Array.from({ length: room.count }, (_, index) => {
              const roomId = `${room.type}-${index}`;
              const occupants = assignments[roomId] || [];
              const capacity = room.type === 'single' ? 1 : 
                             room.type === 'double' ? 2 :
                             room.type === 'triple' ? 3 : 2;

              return (
                <div key={roomId} className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-center mb-2">
                    <h5 className="font-medium">
                      Chambre {room.type === 'single' ? 'Simple' :
                              room.type === 'double' ? 'Double' :
                              room.type === 'triple' ? 'Triple' : 'Suite'} #{index + 1}
                    </h5>
                    <span className="text-sm text-gray-600">
                      {occupants.length}/{capacity}
                    </span>
                  </div>
                  <div className="space-y-1">
                    {occupants.map(id => {
                      const traveler = travelers[parseInt(id.split('-')[1])];
                      return (
                        <p key={id} className="text-sm">
                          {traveler.firstName} {traveler.lastName}
                        </p>
                      );
                    })}
                    {occupants.length < capacity && (
                      <p className="text-sm text-gray-400 italic">
                        {capacity - occupants.length} place{capacity - occupants.length > 1 ? 's' : ''} disponible{capacity - occupants.length > 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                </div>
              );
            })
          ))}
        </div>
      </div>
    </div>
  );
}